import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import { galleryContent, galleryItems } from "../data/siteData";
import "../styles/Gallery.css";

/*
 * Self-contained modern "Gallery" section — a bento-style photo grid
 * (no category filters). Click any photo to open a full-size lightbox
 * with prev/next + ESC to close.
 *
 * The lightbox is rendered through a portal straight into
 * document.body. This is intentional: the routed page wrapper
 * (.tp-page-transition) plays a fade/slide animation on every
 * navigation, and an element with an active `transform` creates a new
 * CSS stacking context — which was trapping the lightbox's z-index
 * below the fixed navbar. Portaling escapes that context entirely, so
 * the lightbox reliably sits above the navbar no matter where in the
 * page tree <Gallery /> is used.
 *
 * Pass a custom `items` array as a prop to reuse this elsewhere (e.g.
 * the full Gallery page); otherwise it defaults to the Home page set.
 */
export default function Gallery({
  items = galleryItems,
  eyebrow = galleryContent.eyebrow,
  heading = galleryContent.heading,
  sub = galleryContent.sub,
  seeAllLabel = galleryContent.seeAllLabel,
  seeAllPath = "/gallery",
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const showPrev = () =>
    setLightboxIndex((prev) =>
      prev === null ? null : (prev - 1 + items.length) % items.length
    );

  const showNext = () =>
    setLightboxIndex((prev) => (prev === null ? null : (prev + 1) % items.length));

  // Keyboard support for the lightbox: Esc closes, arrows navigate.
  useEffect(() => {
    if (lightboxIndex === null) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") showPrev();
      if (event.key === "ArrowRight") showNext();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, items.length]);

  const activeImage = lightboxIndex !== null ? items[lightboxIndex] : null;

  // Bento variant pattern — every 6 items, one runs large (2x2) and one
  // runs wide (2x1) so the grid doesn't feel like a flat, repetitive table.
  const getVariant = (index) => {
    const pos = index % 6;
    if (pos === 0) return "tp-gallery__item--large";
    if (pos === 3) return "tp-gallery__item--wide";
    return "";
  };

  return (
    <section className="tp-section tp-gallery" id="gallery">
      <div className="tp-container">
        <div className="tp-gallery__header">
          <div>
            <span className="tp-eyebrow">{eyebrow}</span>
            <h2 className="tp-section-heading">{heading}</h2>
            <p className="tp-section-sub">{sub}</p>
          </div>
        </div>

        <div className="tp-gallery__grid">
          {items.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={`tp-gallery__item ${getVariant(index)}`}
              onClick={() => openLightbox(index)}
              aria-label={`View ${item.title} full size`}
            >
              <img src={item.image} alt={item.title} loading="lazy" />
              <span className="tp-gallery__overlay">
                <span className="tp-gallery__overlay-title">{item.title}</span>
                <span className="tp-gallery__zoom">
                  <Icon name="search" size={16} />
                </span>
              </span>
            </button>
          ))}

          {items.length === 0 && (
            <p className="tp-gallery__empty">No pieces to show yet.</p>
          )}
        </div>

        {seeAllPath && (
          <Link to={seeAllPath} className="tp-testimonial__link">
            {seeAllLabel}
            <Icon name="arrow" size={15} />
          </Link>
        )}
      </div>

      {activeImage &&
        createPortal(
          <div
            className="tp-gallery__lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={activeImage.title}
            onClick={closeLightbox}
          >
            <button
              className="tp-gallery__lightbox-close"
              aria-label="Close"
              onClick={closeLightbox}
            >
              <Icon name="close" size={22} />
            </button>

            <button
              className="tp-gallery__lightbox-nav tp-gallery__lightbox-nav--prev"
              aria-label="Previous image"
              onClick={(event) => {
                event.stopPropagation();
                showPrev();
              }}
            >
              <Icon name="chevronLeft" size={26} />
            </button>

            <figure
              className="tp-gallery__lightbox-figure"
              onClick={(event) => event.stopPropagation()}
            >
              <img src={activeImage.image} alt={activeImage.title} />
              <figcaption>
                <strong>{activeImage.title}</strong>
                <span>
                  {lightboxIndex + 1} / {items.length}
                </span>
              </figcaption>
            </figure>

            <button
              className="tp-gallery__lightbox-nav tp-gallery__lightbox-nav--next"
              aria-label="Next image"
              onClick={(event) => {
                event.stopPropagation();
                showNext();
              }}
            >
              <Icon name="chevronRight" size={26} />
            </button>
          </div>,
          document.body
        )}
    </section>
  );
}
